/**
 * Auth — Entra B2B + Conditional Access MFA from day one.
 * Per Portal_Frontend_v2.1.1_Spec.md §8 + Reporting_v4_Spec.md §8 (recipient grants).
 *
 * Strategy:
 *  - PublicClientApplication wraps the app via MsalProvider (in main.tsx)
 *  - All routes gated by <AuthRoute> using msal-react's useIsAuthenticated
 *  - In dev mode, a bypass returns a stub admin user so the UI loads without Azure
 *  - In prod, /api/auth/me returns the validated user identity (roles, PHI-tier
 *    capability, tenant scope, optional recipient_grant for external recipients)
 */
import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import type { PortalRole } from '@/types/index';

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || 'dev-mode-client-id',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'common'}`,
    redirectUri: `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: `${window.location.origin}/login`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Per spec §19: PHI cache discipline. sessionStorage = tab-scoped, cleared
    // on tab close — strictest setting that still allows MSAL's redirect flow
    // to complete (memoryStorage + storeAuthStateInCookie:false breaks the
    // post-Entra callback with "in_mem_redirect_unavailable" — caught
    // rev37 Stage 1 testing). Tokens never hit disk.
    cacheLocation: 'sessionStorage',
    // 2FA API-token layer: acquireTokenSilent for the Portal API scope renews
    // through a hidden iframe, and the interim auth state must survive that
    // round trip. storeAuthStateInCookie:true persists ONLY the short-lived
    // nonce/state (not tokens) in a temporary cookie, which is what makes the
    // silent renewal reliable in browsers that partition iframe storage. This
    // is the MSAL-recommended pairing for silent token acquisition and is the
    // minimal change needed for getApiToken() to renew without forcing a full
    // interactive redirect on every API call. Tokens themselves still live only
    // in tab-scoped sessionStorage.
    storeAuthStateInCookie: true,
  },
  system: {
    // The login flow stays redirect-based, but the API-token layer needs the
    // hidden-iframe silent renewal path to actually run. loadFrameTimeout:0
    // previously suppressed that path entirely, which is hostile to
    // acquireTokenSilent renewing an API access token. A real timeout lets the
    // silent iframe complete; if it cannot, getApiToken() catches
    // InteractionRequiredAuthError and falls back to an explicit redirect, so
    // we still get an explicit re-auth signal rather than a silent failure.
    iframeHashTimeout: 6_000,
    loadFrameTimeout: 10_000,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize once at module load — required by msal-browser v3
let initPromise: Promise<void> | null = null;
export async function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = msalInstance.initialize();
  }
  return initPromise;
}

/**
 * Login scopes — the minimum needed at login.
 * Tier-2 / Tier-3 PHI access requires a separate /api/phi/intent flow.
 */
export const loginRequest = {
  scopes: ['User.Read', 'profile', 'openid', 'email'],
};

/**
 * API token request — the scope used to mint the Entra access token that the
 * 2FA gate accepts as a second factor (Authorization: Bearer). This is the
 * Portal Entra app's own exposed API scope (client id
 * cc091855-ce5b-45dd-99b0-24ae3e711827), with the SPA pre-authorized against
 * the same app so consent is implicit. Kept separate from loginRequest because
 * acquireTokenSilent must request exactly this resource scope, not the login
 * graph scopes. Consumed by src/lib/apiClient.ts → getApiToken().
 */
export const apiTokenRequest = {
  scopes: ['api://cc091855-ce5b-45dd-99b0-24ae3e711827/access_as_user'],
};

/**
 * User identity returned by /api/auth/me after JWT validation.
 *
 * Per spec §8 RBAC + Reporting_v4_Spec.md §8 (recipient grants).
 */
export interface PortalUser {
  userId: string;          // Entra B2B object_id
  email: string;
  displayName: string;
  tenant: string;          // tenant slug (matches dim_tenant.tenant_id)
  role: PortalRole;        // viewer | analyst | admin | super_admin | demo_operator
  phiCapability: 'none' | 'tier_1' | 'tier_2' | 'tier_3';
  /** Active phi_access_intent_id, if user has minted one */
  phiAccessIntentId: string | null;
  /** Recipient grants the user holds (per Reporting_v4_Spec.md §8 — Persona H) */
  recipientGrants: Array<{ grantId: string; exportId: string; expiresAt: string }>;
}

/** Stub user for dev-mode bypass (when not connected to real Entra). */
export const DEV_USER: PortalUser = {
  userId: 'dev-user-00000',
  email: 'dev@claridynamics.local',
  displayName: 'Dev User',
  tenant: 'alignment',
  role: 'admin',
  phiCapability: 'tier_3',
  phiAccessIntentId: null,
  recipientGrants: [],
};

/** Returns true when we're in dev-mode auth bypass. */
export function isDevAuthBypass(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_ENTRA_CLIENT_ID;
}

/**
 * Login wall (Stage 2, rev37+) — when `true`, AuthRoute requires a valid MSAL
 * session even when the HMAC signed-link cookie is present. This is the actual
 * "you must log in with CDX before you can see anything" gate the project
 * needs (William: "all public cannot see or access the data at all without a
 * log-in and password (i.e., login into CDX)").
 *
 * When `false` (default), AuthRoute keeps the prior behavior: signed-link
 * cookie alone is enough. This lets us deploy the MSAL plumbing (Stage 1) and
 * verify /login works against a live Entra app before activating the wall.
 *
 * Flip by setting `VITE_REQUIRE_MSAL_AUTH=true` in the build env (see
 * infra/deploy.ps1). Always false in dev — DEV bypass takes precedence.
 */
/**
 * True on a fully synthetic DEMO tenant, where the signed-link cookie alone is
 * enough and no Microsoft sign-in should ever be shown. infra/deploy.ps1 bakes
 * VITE_DEMO_BYPASS_MSAL='true' only when tenant-config demo.isDemo is true, so
 * this is always false on Alignment and any other real tenant.
 */
export function isDemoTenantBypass(): boolean {
  return import.meta.env.VITE_DEMO_BYPASS_MSAL === 'true';
}

export function isMsalAuthRequired(): boolean {
  if (isDevAuthBypass()) return false;
  // Demo-tenant carve-out (William 2026-06-28): a fully synthetic demo tenant
  // (tenant-config demo.isDemo=true) is meant to be shared via its signed link
  // WITHOUT a CDX/Microsoft sign-in, so the HMAC signed-link cookie alone is
  // sufficient and AuthRoute skips the Entra wall. infra/deploy.ps1 bakes
  // VITE_DEMO_BYPASS_MSAL='true' ONLY for such tenants; production (alignment,
  // no demo block) never sets it, so the sign-in wall stays fully on there. The
  // demo SWA also keeps TENANT_2FA_REQUIRED=false, so the data API is cookie-only
  // (no Entra bearer needed) — a frontend-only exemption is therefore sufficient.
  if (import.meta.env.VITE_DEMO_BYPASS_MSAL === 'true') return false;
  return import.meta.env.VITE_REQUIRE_MSAL_AUTH === 'true';
}
